import { PaymentService, TransactionsService } from "@/services";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import CompanyService from "@/services/company_services";
import {
  appendReturnUrl,
  getServiceConfirmCallback,
} from "@/utils/helpers/getCallBackUrl";
import {
  CreateTransactionPayload,
  PaymentChannel,
  PaymentMethod,
  TransactionType,
} from "@/types/transactions";
import { BNPModal } from "@/components/bnp-ui/overlays/BNPModal";
import parseHttpError from "@/utils/parseHttpError";

interface ServicePaypointProps {
  target_resource_id: string;
  service_id: string;
  service_units: number;
  onClose?: () => void; // Added callback to properly handle closing within the UI
  returnUrl?: string;
  isBundlePayment?: boolean;
  isOpen?: boolean;
  /**
   * Optional marker appended to the payment callback URL so the confirmation
   * page can attribute the completed payment (e.g. "live_inspection").
   */
  paymentPurpose?: string;
}

type ScreenState = "loading" | "error" | "checkout" | "processing";

export const ServicePaypoint: React.FC<ServicePaypointProps> = ({
  target_resource_id,
  service_id,
  service_units,
  onClose,
  returnUrl,
  isBundlePayment = false,
  isOpen = true,
  paymentPurpose,
}) => {
  const [screenState, setScreenState] = useState<ScreenState>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const [serviceData, setServiceData] = useState<any>(null);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    isBundlePayment ? "bundle" : "gateway",
  );
  const bundleInitiatedRef = useRef(false);

  const loadCheckoutData = async () => {
    try {
      setScreenState("loading");
      setErrorMessage("");

      const [serviceRes, walletRes] = await Promise.all([
        CompanyService.getServiceByServiceId(service_id),
        TransactionsService.GetWallet(),
      ]);

      setServiceData(serviceRes.data?.data || serviceRes.data);
      setWalletBalance(
        Number(walletRes.data?.data?.wallet?.available_balance || 0),
      );

      setScreenState("checkout");
    } catch (error: any) {
      setErrorMessage(
        parseHttpError(error).message || "Unable to load payment details.",
      );
      setScreenState("error");
    }
  };

  useEffect(() => {
    loadCheckoutData();
  }, [service_id, target_resource_id, service_units]);

  const handleProceedPayment = async () => {
    try {
      setScreenState("processing");

      const callbackUrl = getServiceConfirmCallback(returnUrl, paymentPurpose);
      // When a purpose marker is set, also carry the target resource so the
      // confirmation page can attribute the completed payment (analytics).
      const markedCallbackUrl =
        paymentPurpose && target_resource_id
          ? `${callbackUrl}&target_resource_id=${encodeURIComponent(
              target_resource_id,
            )}`
          : callbackUrl;

      const payload: CreateTransactionPayload = {
        transaction_type: TransactionType.SERVICE_PAYMENT,
        channel:
          paymentMethod === "wallet"
            ? PaymentChannel.WALLET
            : paymentMethod === "bundle"
              ? PaymentChannel.BUNDLE
              : PaymentChannel.CARD,
        service_id,
        service_units,
        payment_method: paymentMethod,
        target_resource_id,
        service_label: serviceData?.service_label,
        transaction_narration: `Payment for ${service_units} unit(s) of ${
          serviceData?.service_label || "service"
        }`,
        callback_url: `${markedCallbackUrl}`,
      };

      const transactionResponse =
        await TransactionsService.CreateNewTransaction(payload);
      const transaction = transactionResponse.data?.data;

      if (paymentMethod === "wallet" || paymentMethod === "bundle") {
        window.location.href = transaction.callback_url;
        return;
      }

      const initializationResponse = await PaymentService.InitializePayment(
        transaction.reference,
      );

      const checkoutUrl =
        initializationResponse.data?.data?.authorization_url ||
        initializationResponse.data?.authorization_url;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error("Unable to start payment.");
      }
    } catch (error: any) {
      toast.error(
        parseHttpError(error).message ||
          "Something went wrong while processing your payment.",
      );
      setScreenState(isBundlePayment ? "error" : "checkout");
    }
  };

  useEffect(() => {
    if (
      isBundlePayment &&
      screenState === "checkout" &&
      !bundleInitiatedRef.current
    ) {
      bundleInitiatedRef.current = true;
      handleProceedPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBundlePayment, screenState]);
  let content: React.ReactNode;

  if (screenState === "loading" || screenState === "processing") {
    content = (
      <div className="flex flex-col items-center justify-center text-center py-12 px-4 min-h-[280px]">
        <div className="relative w-10 h-10 mb-4">
          <div className="w-10 h-10 rounded-full border-2 border-slate-100 border-t-[#2d96a7] animate-spin" />
        </div>
        <p className="text-slate-800 text-sm font-semibold tracking-tight">
          {screenState === "processing"
            ? "Preparing your payment..."
            : "Loading payment details..."}
        </p>
        <p className="text-slate-400 text-xs mt-1">Please wait a moment</p>
      </div>
    );
  } else if (screenState === "error") {
    content = (
      <div className="flex flex-col items-center justify-center text-center py-10 px-6 min-h-[280px] max-w-sm mx-auto">
        <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-600 font-bold text-lg mb-4 shrink-0">
          !
        </div>
        <h3 className="text-base font-semibold text-slate-900 mb-1 leading-snug">
          We couldn&apos;t load this payment page
        </h3>
        <p className="text-xs text-slate-500 mb-6 leading-relaxed">
          {errorMessage}
        </p>
        <button
          type="button"
          onClick={() => {
            if (isBundlePayment) {
              bundleInitiatedRef.current = false;
              setScreenState("checkout");
            } else {
              loadCheckoutData();
            }
          }}
          className="inline-flex items-center justify-center px-6 h-10 rounded-xl bg-[#2d96a7] hover:bg-[#257f8d] active:scale-[0.98] text-white text-xs font-semibold transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2d96a7]/30"
        >
          Try Again
        </button>
      </div>
    );
  } else if (isBundlePayment) {
    content = (
      <div className="flex flex-col items-center justify-center text-center py-12 px-4 min-h-[280px]">
        <div className="relative w-10 h-10 mb-4">
          <div className="w-10 h-10 rounded-full border-2 border-slate-100 border-t-[#2d96a7] animate-spin" />
        </div>
        <p className="text-slate-800 text-sm font-semibold tracking-tight">
          Preparing your payment...
        </p>
        <p className="text-slate-400 text-xs mt-1">Setting up your bundle</p>
      </div>
    );
  } else {
    const pricePerUnit = Number(serviceData?.amount || 0);
    const totalCalculatedAmount = pricePerUnit * service_units;
    const isWalletInsufficient = walletBalance < totalCalculatedAmount;

    content = (
      <div className="">
        {/* Integrated Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Close modal"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        <div className="flex flex-col md:flex-row">
          {/* LEFT PANEL: PAYMENT OPTIONS */}
          <div className="w-full md:w-7/12 p-6 md:p-8 flex flex-col justify-between">
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                  Complete Your Payment
                </h2>
                <p className="text-slate-500 text-xs mt-1">
                  Select your preferred payment channel below.
                </p>
              </div>

              <div className="space-y-3 mb-8">
                {/* CARD / BANK GATEWAY */}
                <label
                  onClick={() => setPaymentMethod("gateway")}
                  className={`group flex items-start gap-3.5 p-4 rounded-xl border text-left cursor-pointer transition-all duration-200 ${
                    paymentMethod === "gateway"
                      ? "border-[#2d96a7] bg-[#2d96a7]/[0.03] ring-1 ring-[#2d96a7]"
                      : "border-slate-200/80 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center mt-0.5 shrink-0 transition-colors ${
                      paymentMethod === "gateway"
                        ? "border-[#2d96a7]"
                        : "border-slate-300"
                    }`}
                  >
                    {paymentMethod === "gateway" && (
                      <div className="w-2 h-2 rounded-full bg-[#2d96a7]" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-slate-900">
                      Pay with Card / Bank
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                      Fast and secure checkout via online payment gateway.
                    </p>
                  </div>
                </label>

                {/* WALLET OPTION */}
                <label
                  onClick={() =>
                    !isWalletInsufficient && setPaymentMethod("wallet")
                  }
                  className={`group flex items-start gap-3.5 p-4 rounded-xl border text-left transition-all duration-200 ${
                    isWalletInsufficient
                      ? "opacity-60 cursor-not-allowed bg-slate-50/60 border-slate-200"
                      : paymentMethod === "wallet"
                        ? "border-[#2d96a7] bg-[#2d96a7]/[0.03] ring-1 ring-[#2d96a7] cursor-pointer"
                        : "border-slate-200/80 hover:border-slate-300 bg-white cursor-pointer"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center mt-0.5 shrink-0 transition-colors ${
                      paymentMethod === "wallet"
                        ? "border-[#2d96a7]"
                        : "border-slate-300"
                    }`}
                  >
                    {paymentMethod === "wallet" && (
                      <div className="w-2 h-2 rounded-full bg-[#2d96a7]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center gap-2">
                      <h4 className="text-xs font-semibold text-slate-900 truncate">
                        Wallet Balance
                      </h4>
                      <span
                        className={`text-xs font-bold shrink-0 ${isWalletInsufficient ? "text-rose-600" : "text-slate-900"}`}
                      >
                        ₦
                        {walletBalance.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                      {isWalletInsufficient ? (
                        <span className="text-rose-600 font-medium">
                          Insufficient balance to complete this transaction.
                        </span>
                      ) : (
                        "Instantly debit from your account wallet balance."
                      )}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <button
              onClick={handleProceedPayment}
              className="w-full h-12 rounded-xl bg-[#2d96a7] hover:bg-[#257f8d] active:scale-[0.99] text-white text-xs font-semibold transition-all duration-200 shadow-sm"
            >
              Pay ₦
              {totalCalculatedAmount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </button>
          </div>

          {/* RIGHT PANEL: ORDER SUMMARY */}
          <div className="w-full md:w-5/12 bg-[#2d96a7] text-white p-6 md:p-8 flex flex-col justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-gray-100 font-semibold block mb-1">
                Amount Due
              </span>
              <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                ₦
                {totalCalculatedAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </h2>

              <div className="border-t border-white/10 mt-6 pt-5 space-y-3">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="font-semibold text-xs text-white">
                      {serviceData?.service_label || "Selected Service"}
                    </p>
                    <p className="text-gray-100 text-[11px] mt-0.5">
                      Quantity: {service_units}
                    </p>
                  </div>
                  <p className="font-semibold text-xs text-white whitespace-nowrap">
                    ₦{totalCalculatedAmount.toLocaleString()}
                  </p>
                </div>

                {serviceData?.description && (
                  <p className="text-gray-100 text-[11px] leading-relaxed line-clamp-3 bg-white/5 p-3 rounded-lg">
                    {serviceData.description}
                  </p>
                )}
              </div>
            </div>

            {/* Pricing breakdown */}
            <div className="space-y-2.5 text-xs border-t border-white/10 pt-5 mt-8">
              <div className="flex justify-between text-gray-100 text-[11px]">
                <span>Price per Unit</span>
                <span className="text-white font-medium">
                  ₦{pricePerUnit.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-gray-100 text-[11px]">
                <span>Units</span>
                <span className="text-white font-medium">{service_units}</span>
              </div>
              <div className="flex justify-between font-semibold text-xs pt-3 border-t border-white/10 text-white">
                <span>Total Cost</span>
                <span className="text-white">
                  ₦
                  {totalCalculatedAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <BNPModal isOpen={isOpen} onClose={onClose ?? (() => {})}>
      {content}
    </BNPModal>
  );
};
